/**
 * HAW (Human-Agent Ways of Working) mode strings. Ported from the
 * vanilla XAES content registry at `_legacy/xaes-content.js`:
 *
 *   - `XAES_CONTENT.ws4.haw.modes` (lines 26-70) — full mode data
 *     (title, subtitle, badge, description, accent) surfaced on the
 *     hover tooltip + on the HAW modal cards.
 *   - `XAES_CONTENT.ws4.bubbles` (lines 155-186) — short hover-tip
 *     copy used by other diagrams; superseded for HAW by the richer
 *     4-field tooltip in `_legacy/wd.html` `#haw-bubble-tooltip`
 *     (lines 2567-2641).
 *
 * Vanilla wires the four outer SVG groups in this layout (see
 * `_legacy/wd.html` lines 765-812):
 *
 *   bubble-tl (TL, blue)   → Human In-The-Loop    → MODE 1
 *   bubble-tr (TR, orange) → Humans Above-The-Loop → MODE 3
 *   bubble-bl (BL, yellow) → Human On-The-Loop    → MODE 2
 *   bubble-br (BR, melon)  → Out-of-The-Loop      → MODE 4
 *   bubble-c  (centre)     → Agentic Enterprise
 *
 * Accent is per-SLOT, not per-mode — vanilla's `XAES.HAW_ACCENT`
 * (`_legacy/xaes.js` lines 144-157) keys by bubble-tl / bubble-tr /
 * bubble-bl / bubble-br, mapping each to a fixed DXC accent. The
 * mode-level `accentVar` field is used by the HAW modal cards (not
 * the tooltip).
 */
export type HawModeId =
  | "tl"
  | "tr"
  | "bl"
  | "br"
  | "centre";

export type HawMode = {
  id: HawModeId;
  title: string;
  /**
   * Three-clause one-liner shown under the title in the tooltip
   * (e.g. "High oversight · Human decides · Agent assists"). Centre
   * doesn't have one in vanilla.
   */
  subtitle?: string;
  /** Small uppercase eyebrow ("MODE 1"..."MODE 4"). Centre has none. */
  badge?: string;
  /** Long-form description — the tooltip's body. */
  body: string;
  /**
   * Tooltip + ring accent for this slot, ported from
   * `XAES.HAW_ACCENT` in `_legacy/xaes.js`. Resolved at runtime so it
   * theme-swaps automatically.
   */
  accent: string;
  /**
   * String id for the bubble's lucide icon — mapped to a component in
   * `HawPanel.tsx`'s `ICON_FOR` registry. Kept as a string (rather
   * than the component itself) so this module stays data-only.
   *
   * Legacy used Material Symbols Outlined glyphs:
   *   tl person  → User
   *   tr groups  → Users
   *   bl visibility → Eye
   *   br smart_toy  → Bot
   *
   * Centre bubble has no icon.
   */
  lucide?: "user" | "users" | "eye" | "bot";
};

/**
 * Outer-bubble ordering matches the visual TL / TR / BL / BR slots
 * in `_legacy/wd.html`. The centre entry sits last so consumers can
 * iterate the four outer bubbles via `slice(0, 4)`.
 *
 * Strings copied verbatim from vanilla — do not paraphrase.
 */
export const HAW_MODES: HawMode[] = [
  {
    id: "tl",
    title: "Human In-The-Loop",
    subtitle: "High oversight · Human decides · Agent assists",
    badge: "MODE 1",
    body:
      "The human is actively involved in every step of the workflow. The AI agent surfaces information, generates options, or executes sub-tasks — but all decisions and approvals pass through the human. Appropriate for high-stakes, regulated, or novel situations where human judgement is non-negotiable.",
    accent: "var(--accent-true-blue)",
    lucide: "user",
  },
  {
    id: "tr",
    title: "Humans Above-The-Loop",
    subtitle: "Strategic oversight · Agents operate · Humans set direction",
    badge: "MODE 3",
    body:
      "Humans operate at the strategic level — setting goals, defining constraints, and reviewing aggregate outcomes — while AI agents manage end-to-end execution across entire workflows or processes. Human involvement shifts from task-level to governance-level, enabling scale without sacrificing accountability.",
    accent: "var(--accent-gold)",
    lucide: "users",
  },
  {
    id: "bl",
    title: "Human On-The-Loop",
    subtitle: "Monitor & intervene · Agent executes · Human oversees",
    badge: "MODE 2",
    body:
      "The AI agent executes tasks autonomously within defined parameters, while the human monitors outcomes and retains the ability to intervene, override, or escalate. Best suited for repeatable workflows where speed is valued and human oversight acts as a safety net rather than a gatekeeper.",
    accent: "var(--accent-peach)",
    lucide: "eye",
  },
  {
    id: "br",
    title: "Out-Of-The-Loop",
    subtitle: "Full autonomy · Agent decides & acts · Async reporting",
    badge: "MODE 4",
    body:
      "AI agents operate fully autonomously within pre-approved boundaries — sensing, deciding, and acting without real-time human involvement. Humans receive periodic reports and define the guardrails, but do not participate in execution. Reserved for well-understood, low-risk, high-volume processes where trust in the agent has been established over time.",
    accent: "var(--accent-melon)",
    lucide: "bot",
  },
  {
    id: "centre",
    title: "Agentic Enterprise",
    body:
      "The destination state — an organisation where AI agents and humans collaborate fluidly, each operating at the level of autonomy best suited to the task and context. The Human-Agent Ways of Working model defines the spectrum, and each workflow is matched to the mode that fits it.",
    accent: "var(--accent-sky)",
  },
];
